import texts from '@components/Calculators/LoanEmi/en.json';
import { theme } from '@ui/tailwind.config';

export const personalLoanCalculatorDefaultValues = {
    loanAmount: 500000,
    rate: 12.5,
    time: 5,
};

export const carLoanCalculatorDefaultValues = {
    loanAmount: 500000,
    rate: 9,
    time: 7,
    downpayment: 10,
};

export const homeLoanCalculatorDefaultValues = {
    loanAmount: 5000000,
    rate: 9,
    time: 20,
    downpayment: 10,
};

const loanEmiCalculatorFields = {
    loanAmount: {
        id: 'loanAmount',
        fieldName: texts?.LoanAmount,
        prefix: '₹',
        width: 'w-[107px] text',
        sliderInterval: 50000,
        minValue: 100000,
        maxValue: 100000000,
        maxLength: 12,
        shouldFormatValue: true,
        className: 'text-left',
        showLimit: true,
    },
    interestRate: {
        id: 'interestRate',
        fieldName: texts?.InterestRate,
        suffix: '%',
        width: 'w-10',
        sliderInterval: 1,
        minValue: 1,
        maxValue: 30,
        maxLength: 5,
        showLimit: true,
    },
    loanTenure: {
        id: 'loanTenure',
        fieldName: texts?.LoanTenure,
        suffix: 'years',
        suffixSingular: 'year',
        width: 'w-10',
        sliderInterval: 1,
        minValue: 1,
        maxValue: 10,
        maxLength: 3,
        shouldFormatValue: true,
        showLimit: true,
    },
};

export const loanCalculatorTypes = {
    PERSONAL_LOAN: 'personalLoan',
    CAR_LOAN: 'carLoan',
    HOME_LOAN: 'homeLoan',
};

const getPersonalLoanCalculatorFields = ({ loanAmount, rate, time }) => {
    return {
        loanAmount: {
            ...loanEmiCalculatorFields.loanAmount,
            value: loanAmount,
            sliderInterval: 1000,
            minValue: 30000,
            maxValue: 1000000,
        },
        interestRate: {
            ...loanEmiCalculatorFields.interestRate,
            value: rate,
        },
        loanTenure: { ...loanEmiCalculatorFields.loanTenure, value: time },
    };
};

const getCarLoanCalculatorFields = ({ loanAmount, rate, time }) => {
    return {
        loanAmount: {
            ...loanEmiCalculatorFields.loanAmount,
            fieldName: texts?.LoanAmount,
            value: loanAmount,
            maxValue: 10000000,
            sliderInterval: 50000,
        },
        interestRate: {
            ...loanEmiCalculatorFields.interestRate,
            value: rate,
        },
        loanTenure: { ...loanEmiCalculatorFields.loanTenure, value: time },
    };
};

const getHomeLoanCalculatorFields = ({ loanAmount, rate, time }) => {
    return {
        loanAmount: {
            ...loanEmiCalculatorFields.loanAmount,
            fieldName: texts?.LoanAmount,
            value: loanAmount,
            sliderInterval: 100000,
        },
        interestRate: {
            ...loanEmiCalculatorFields.interestRate,
            value: rate,
        },
        loanTenure: {
            ...loanEmiCalculatorFields.loanTenure,
            value: time,
            maxValue: 30,
        },
    };
};

export const getLoanCalculatorFields = ({ loanAmount, rate, time, type }) => {
    switch (type) {
        case loanCalculatorTypes.CAR_LOAN:
            return getCarLoanCalculatorFields({
                loanAmount,
                rate,
                time,
            });

        case loanCalculatorTypes.HOME_LOAN:
            return getHomeLoanCalculatorFields({
                loanAmount,
                rate,
                time,
            });
        case loanCalculatorTypes.PERSONAL_LOAN:
            return getPersonalLoanCalculatorFields({
                loanAmount,
                rate,
                time,
            });

        default:
            return getPersonalLoanCalculatorFields({ loanAmount, rate, time });
    }
};

export const getLoanCalculatorName = (type) => {
    switch (type) {
        case loanCalculatorTypes.CAR_LOAN:
            return texts?.CarLoanCalculator;
        case loanCalculatorTypes.HOME_LOAN:
            return texts?.HomeLoanCalculator;
        case loanCalculatorTypes.PERSONAL_LOAN:
            return texts?.PersonalLoanCalculator;
        default:
            return null;
    }
};

export const getLoanName = (type) => {
    switch (type) {
        case loanCalculatorTypes.CAR_LOAN:
            return texts?.CarLoan;
        case loanCalculatorTypes.HOME_LOAN:
            return texts?.HomeLoan;
        case loanCalculatorTypes.PERSONAL_LOAN:
            return texts?.PersonalLoan;
        default:
            return null;
    }
};

export const hasDownpayment = (type) =>
    [loanCalculatorTypes.CAR_LOAN, loanCalculatorTypes.HOME_LOAN].includes(
        type
    );

export const getLoanEmiChartData = (calculatedValue) => [
    {
        name: 'Principal',
        value: calculatedValue.principal,
        color: theme.colors.primary?.[500],
    },
    {
        name: 'Total interest',
        value: calculatedValue.totalInterest,
        color: theme.colors.primary?.[70],
    },
];
